// The week's "Sopa" row, shared by the week view and the meal plan (as Android's PotActions).
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import EventIcon from '@mui/icons-material/Event';
import ListIcon from '@mui/icons-material/List';
import ReplayIcon from '@mui/icons-material/Replay';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import * as api from '../plannerApi';
import * as T from '../types';
import { Panel, useNotify, useRun, useUndoable } from '../common';
import { dayLabel, number, quantity, today, unitShort } from '../labels';
import { useSheets } from '../tasks';

/** "domingo" (the weekday alone, lower case). */
const weekday = (date: T.DateOnly) => dayLabel(date, false).toLowerCase();

const potTitle = (pot: T.PotCooking) => (pot.scale === 1 ? pot.recipeName : `${pot.recipeName} ×${number(pot.scale, 1)}`);

/** "fazer domingo", "feita domingo · até quinta", "feita domingo · acabou terça". */
function potSubtitle(pot: T.PotCooking): string {
  const now = today();
  if (pot.date > now) return `fazer ${weekday(pot.date)}`;
  if (pot.finishedOn) return `feita ${weekday(pot.date)} · acabou ${weekday(pot.finishedOn)}`;
  return pot.ends < now ? `feita ${weekday(pot.date)} · já deve ter acabado` : `feita ${weekday(pot.date)} · até ${weekday(pot.ends)}`;
}

/** "Não saiu do stock: Batata (2 un na receita, stock em kg)", or null. */
function potNotTaken(pot: T.PotCooking): string | null {
  if (!pot.notTaken?.length) return null;
  return 'Não saiu do stock: '
    + pot.notTaken.map(n => `${n.name} (${quantity(n.quantity, n.unit)} na receita, stock em ${unitShort(n.stockUnit)})`).join(', ');
}

/** Made, and not marked finished. */
const isGoing = (pot: T.PotCooking) => pot.date <= today() && !pot.finishedOn;

/** "Marcar sopa" and a pot's menu, for the "Sopa" row and the week's "Sem sopa" alert; render `element`. */
export function useSoupActions(week: T.PotWeek | undefined, onChanged: () => unknown) {
  const sheets = useSheets();
  const suggestions = week?.suggestions ?? [];
  const run = useRun();
  const notify = useNotify();
  const offerUndo = useUndoable();

  const changed = async (work: () => Promise<unknown>, message?: string, undo?: () => Promise<unknown>) => {
    if (await run(work) === undefined) return;
    await onChanged();
    if (!message) return;
    if (undo) offerUndo(message, undo, onChanged);
    else notify(message);
  };

  /** The suggestions in one tap, any other recipe under "Outra receita…". */
  const chooseRecipe = async (title: string): Promise<T.RecipeSide | null> => {
    const choice = await sheets.choose(title, [
      ...suggestions.map((r, i) => ({ value: r.publicId, text: r.name, detail: i === 0 ? 'A última que fizeste' : undefined, icon: <SoupKitchenIcon /> })),
      { value: 'other', text: 'Outra receita…', detail: 'Qualquer receita do livro', icon: <ListIcon /> },
    ], suggestions.length === 0 ? 'Marca uma receita como "Faz-se em panela" para aparecer aqui.' : null);
    if (choice === null) return null;
    if (choice !== 'other') return suggestions.find(s => s.publicId === choice) ?? null;
    const recipes = await run(() => api.getRecipes());
    if (!recipes) return null;
    const list = recipes.filter(r => r.course !== 'Side').sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    const picked = await sheets.choose(title, list.map(r => ({ value: r.publicId, text: r.name, detail: r.isPotDish ? 'em panela' : undefined })));
    const recipe = list.find(r => r.publicId === picked);
    return recipe ? { publicId: recipe.publicId, name: recipe.name } : null;
  };

  const mark = async () => {
    const recipe = await chooseRecipe('Que sopa?');
    if (!recipe) return;
    // Today, or the first day of a week still to come
    const start = (week?.weekStart ?? today()).substring(0, 10);
    const day = await sheets.chooseDay('Quando a fazes?', start > today() ? start : today());
    if (!day) return;
    await changed(() => api.createPot({ recipePublicId: recipe.publicId, date: day }), `${recipe.name} marcada para ${weekday(day)}`);
  };

  const menu = async (pot: T.PotCooking) => {
    const going = isGoing(pot);
    const choice = await sheets.choose(potTitle(pot), [
      ...(going ? [{ value: 'finished', text: 'Acabou a sopa', detail: 'Já não há; ajuda a saber quanto dura', icon: <CheckIcon />, primary: true }] : []),
      ...(pot.finishedOn ? [{ value: 'going', text: 'Ainda há', detail: 'Desmarca o "acabou"', icon: <ReplayIcon /> }] : []),
      { value: 'day', text: 'Mudar o dia', detail: dayLabel(pot.date), icon: <EventIcon /> },
      { value: 'recipe', text: 'Outra sopa', detail: pot.recipeName, icon: <EditIcon /> },
      { value: 'cancel', text: 'Não fiz', detail: pot.stockConsumed ? 'Os ingredientes voltam ao stock' : 'Sai do plano e da lista', icon: <CloseIcon />, danger: true },
    ], potSubtitle(pot));
    switch (choice) {
      case 'finished':
        await changed(() => api.finishPot(pot.publicId, true), 'Acabou a sopa', () => api.finishPot(pot.publicId, false));
        break;
      case 'going':
        await changed(() => api.finishPot(pot.publicId, false));
        break;
      case 'day': {
        const day = await sheets.chooseDay('Quando a fazes?', pot.date);
        if (day && day !== pot.date) await changed(() => api.updatePot(pot.publicId, { recipePublicId: pot.recipePublicId, date: day }));
        break;
      }
      case 'recipe': {
        const recipe = await chooseRecipe('Que sopa?');
        if (recipe && recipe.publicId !== pot.recipePublicId) await changed(() => api.updatePot(pot.publicId, { recipePublicId: recipe.publicId, date: pot.date }));
        break;
      }
      case 'cancel':
        await changed(() => api.cancelPot(pot.publicId, true), `${pot.recipeName} tirada do plano`, () => api.cancelPot(pot.publicId, false));
        break;
    }
  };

  return { mark, menu, element: sheets.element };
}

export function SoupRow({ week, onChanged }: { week: T.PotWeek; onChanged: () => unknown }) {
  const { mark, menu, element } = useSoupActions(week, onChanged);
  return (
    <Panel title="Sopa" action={<Button size="small" startIcon={<AddIcon />} onClick={mark}>Marcar sopa</Button>} sx={{ pb: 1 }}>
      {week.pots.length === 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pb: 0.5 }}>Nenhuma panela marcada esta semana.</Typography>
      )}
      {week.pots.map(pot => (
        <Box
          key={pot.publicId} role="button" tabIndex={0}
          onClick={() => menu(pot)}
          onKeyDown={e => { if (e.key === 'Enter') menu(pot); }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, minHeight: 44, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <SoupKitchenIcon fontSize="small" color={isGoing(pot) ? 'primary' : 'action'} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 550 }}>{potTitle(pot)}</Typography>
            <Typography variant="caption" color="text.secondary">{potSubtitle(pot)}</Typography>
            {potNotTaken(pot) && <Typography variant="caption" color="text.secondary" component="div">{potNotTaken(pot)}</Typography>}
          </Box>
          <ChevronRightIcon fontSize="small" color="action" />
        </Box>
      ))}
      {element}
    </Panel>
  );
}
